import {
  parseUnderlineFlashcard,
  underlineRegexFlashcard,
  replaceOneFlashcard,
  clozeRegexFlashcard,
  parseClozeFlashcard,
  FlashcardType,
} from '@osmosis/shared/quiz';

export interface RenderI {
  text: string;
  type?: FlashcardType;
  randomIndex?: number;
  className: string;
}

type UiReplaceI = Pick<RenderI, 'text' | 'randomIndex' | 'className'> & {
  regex: RegExp;
  type: 'a' | 'u';
};

/**
 * Return text with spaces.
 */
export const blank = (className: RenderI['className']) =>
  `<span class="${className}">${String.fromCharCode(160).repeat(15)}</span>`;

const uiReplaceOne = ({
  text,
  regex,
  className,
  randomIndex = -1,
  type,
}: UiReplaceI): string => {
  const isUnderlineType = type === 'u';
  const matches = text.match(regex);

  if (matches && randomIndex > -1) {
    const data = matches[randomIndex];
    const replacer = isUnderlineType
      ? data.match(underlineRegexFlashcard)
      : data.match(/{{([(\w\W)]+?)::([(\w\W)]+?)}}/)?.[2];

    return text.replace(data, `<span class="${className}">${replacer}</span>`);
  }

  return text;
};

/**
 * Return text with blank by type.
 */
export const renderPrompt = ({
  className,
  type,
  text,
  randomIndex,
}: RenderI): string => {
  switch (type) {
    case 'ua':
      return text.replace(underlineRegexFlashcard, blank(className));
    case 'u':
      return replaceOneFlashcard(
        text,
        underlineRegexFlashcard,
        randomIndex,
        blank(className),
      );
    case 'aa':
      return text.replace(clozeRegexFlashcard, blank(className));
    case 'a':
      return parseClozeFlashcard(
        replaceOneFlashcard(
          text,
          clozeRegexFlashcard,
          randomIndex,
          blank(className),
        ),
      );
    default:
      return text;
  }
};

/**
 * Return animated answer by type.
 */
export const renderAnswered = ({
  text,
  type,
  randomIndex,
  className,
}: RenderI): string => {
  switch (type) {
    case 'ua':
    case 'aa': {
      const parseType =
        type === 'ua' ? parseUnderlineFlashcard : parseClozeFlashcard;
      const reg = type === 'ua' ? underlineRegexFlashcard : clozeRegexFlashcard;

      return text.replace(
        reg,
        (match: string) =>
          `<span class="${className}">&nbsp;${parseType(match)}&nbsp;</span>`,
      );
    }
    case 'u':
    case 'a': {
      const parseType =
        type === 'u' ? parseUnderlineFlashcard : parseClozeFlashcard;
      const regex =
        type === 'u' ? underlineRegexFlashcard : clozeRegexFlashcard;

      const txt = uiReplaceOne({ text, regex, className, randomIndex, type });

      return txt.replace(
        regex,
        (match: string) => `<span>&nbsp;${parseType(match)}&nbsp;</span>`,
      );
    }
    default:
      return text;
  }
};
